<template>
  <div>
    <b-row>
      <FileField
        v-model="file"
        :multiple="true"
        class="col-12"
        :label="__('common.file')"
        name="file"
        required
      />

      <TextField
        v-if="!multiple"
        v-model="name"
        name="name"
        :label="__('documents.document-name')"
        class="col-3"
      />

      <SelectField
        v-if="!multiple"
        v-model="type"
        name="type_id"
        :label="__('documents.type')"
        :options="types"
        class="col-3"
      />

      <SelectField
        v-model="documentFolder"
        name="document_folder_id"
        :label="__('documents.folder')"
        :options="documentFolders"
        :class="{'col-3': !multiple, 'col-12': multiple}"
      />

      <TextField
        v-if="!multiple"
        v-model="tag"
        name="tag"
        :label="__('documents.tag')"
        class="col-3"
      />

      <TextAreaField
        v-if="!multiple"
        v-model="description"
        name="description"
        :label="__('common.description')"
        :placeholder="__('documents.description-text')"
        class="col-12"
      />
    </b-row>

    <div class="text-right">
      <b-alert v-if="showEncryptedWarning" show variant="warning" class="text-left mb-2">
        <p class="mb-2">{{ __('documents.encrypted-file-warning') }}</p>
        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" size="sm" class="mr-2" @click="cancelEncryptedUpload">
            {{ __('buttons.cancel') }}
          </b-button>
          <b-button variant="warning" size="sm" @click="confirmEncryptedUpload">
            {{ __('documents.upload-anyway') }}
          </b-button>
        </div>
      </b-alert>

      <b-button v-if="!showEncryptedWarning" variant="primary" size="sm" @click="upload">
        <Icon class="mr-2" icon="upload" size="14" />
        {{ __('buttons.upload') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import ModalMixin from '@/General/Modal/ModalMixin.js'
import DocumentTypeService from '@/Modules/Document/DocumentType/DocumentTypeService.js'

export default {
  name: 'UploadModal',

  mixins: [ ModalMixin ],

  props: {
    meta: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },

  data() {
    return {
      name: '',
      tag: '',
      type: null,
      description: '',
      file: null,
      types: [],
      documentFolders: this.meta.documentFolders || [],
      documentFolder: null,
      showEncryptedWarning: false,
      acknowledgeEncrypted: false,
    }
  },

  computed: {
    multiple: function () {
      return this.file && this.file.length > 1
    }
  },

  watch: {
    file() {
      this.showEncryptedWarning = false
      this.acknowledgeEncrypted = false
    },
  },

  created() {
    DocumentTypeService.all().then((items) => (this.types = items))
    const selectedDocumentFolderIndex = this.documentFolders.findIndex(
      (item) => item.value === this.meta.documentFolderId
    )
    this.documentFolder = this.documentFolders[selectedDocumentFolderIndex]
  },

  methods: {
    upload() {
      this.meta.uploadCallback &&
        this.meta
          .uploadCallback(this.getUploadPayload(), this.multiple)
          .then(() => {
            this.closeModal(this.__(`documents.file-upload-success`))
            this.onSuccess && this.onSuccess()
          })
          .catch((error) => {
            if (this.isEncryptedFileError(error)) {
              this.showEncryptedWarning = true
              return
            }
            this.setErrorsFromResponse(error)
          })
    },

    confirmEncryptedUpload() {
      this.acknowledgeEncrypted = true
      this.showEncryptedWarning = false
      this.upload()
    },

    cancelEncryptedUpload() {
      this.showEncryptedWarning = false
    },

    isEncryptedFileError(error) {
      if (error?.response?.status !== 422) {
        return false
      }
      const encryptedMessage = 'File is encrypted. Please decrypt it and attach unencrypted version.'
      const errors = error.response.data?.errors || {}
      return Object.values(errors).some((messages) => {
        if (!Array.isArray(messages)) {
          return false
        }
        return messages.some(
          (msg) =>
            msg === encryptedMessage ||
            (typeof msg === 'object' && msg?.message === encryptedMessage)
        )
      })
    },

    getUploadPayload() {
      let formData = new FormData()

      if (this.multiple) {
        formData = this.getMultipleFileFormData(formData)
      } else {
        formData = this.getSingleFileFormData(formData)
      }

      if (this.documentFolder) {
        formData.append(
            'document_folder_id',
            this.$selectValue(this, 'documentFolder')
        )
      }

      if (this.acknowledgeEncrypted) {
        formData.append('acknowledge_encrypted', '1')
      }

      return formData
    },

    getSingleFileFormData(formData) {
      formData.append('name', this.name)
      formData.append('tag', this.tag)

      if (this.type) {
        formData.append('type_id', this.type && this.type.value)
      }

      formData.append('description', this.description)

      if (this.file) {
        formData.append('file', this.file[0])
      }

      return formData
    },

    getMultipleFileFormData(formData) {
      this.file.forEach(file => {
        formData.append(`files[]`, file)
      })

      return formData
    },
  },
}
</script>
